import type { OpenlifterBundleFile } from "./types";
import { wrpfHandoffDebug, wrpfHandoffError, maskTokenForLog } from "./wrpfHandoffDebug";

const summarizeResponseBody = (body: string, maxLen = 240): string => {
  const trimmed = body.trim();
  if (trimmed.length === 0) {
    return "(empty body)";
  }
  const preview = trimmed.slice(0, maxLen);
  const suffix = trimmed.length > maxLen ? "…" : "";
  if (trimmed.startsWith("<")) {
    return `HTML response preview: ${preview}${suffix}`;
  }
  return `${preview}${suffix}`;
};

export async function fetchWrpfBundle(bundleUrl: string, token: string): Promise<OpenlifterBundleFile> {
  wrpfHandoffDebug("fetch bundle", { bundleUrl, token: maskTokenForLog(token) });

  const response = await fetch(bundleUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  wrpfHandoffDebug("bundle response", {
    ok: response.ok,
    status: response.status,
    contentType: response.headers.get("content-type"),
  });

  const body = await response.text();

  if (!response.ok) {
    wrpfHandoffError("bundle fetch failed", {
      status: response.status,
      body: summarizeResponseBody(body),
    });
    if (response.status === 401) {
      throw new Error("Tu sesión con WRPF expiró. Pedile al admin que regenere la sesión y abrí el nuevo enlace.");
    }
    throw new Error(`Bundle fetch failed (${response.status}): ${summarizeResponseBody(body)}`);
  }

  try {
    const bundle = JSON.parse(body) as OpenlifterBundleFile;
    wrpfHandoffDebug("bundle parsed", {
      tournamentId: bundle._wrpf?.tournamentId,
      tournamentName: bundle._wrpf?.tournamentName,
      entryCount: bundle.registration?.entries?.length,
    });
    return bundle;
  } catch (parseError) {
    wrpfHandoffError("bundle JSON parse failed", {
      parseError,
      body: summarizeResponseBody(body),
    });
    if (body.trimStart().startsWith("<")) {
      throw new Error(
        `La URL del bundle devolvió HTML en vez de JSON (status ${response.status}). Revisá CORS, BETTER_AUTH_URL y que la sesión no expiró. URL: ${bundleUrl}`,
      );
    }
    throw new Error(
      `Respuesta del bundle no es JSON válido (status ${response.status}): ${summarizeResponseBody(body)}`,
    );
  }
}

export async function postWrpfResults(
  resultsUrl: string,
  token: string,
  payload: unknown,
): Promise<{ accepted: boolean; count: number }> {
  const body = JSON.stringify(payload);
  const response = await fetch(resultsUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body,
  });

  if (!response.ok) {
    const text = await response.text();
    if (response.status === 401) {
      throw new Error(
        "Tu sesión con WRPF expiró. Pedile al admin que regenere la sesión y abrí el nuevo enlace. Podés seguir operando y guardar el .openlifter localmente.",
      );
    }
    throw new Error(`Results POST failed (${response.status}): ${text}`);
  }

  return (await response.json()) as { accepted: boolean; count: number };
}
